import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X, Mic, MessageSquare } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getAssistantUiCopy } from '../i18n/assistantUi';
import { getWsjCommercialWhatsappUrl } from '@/lib/wsjCommercialWhatsapp';
import { getAssistantApiBaseUrl } from '@/config/assistantApi';

const WHATSAPP_URL = getWsjCommercialWhatsappUrl(
  'Olá! Gostaria de saber mais sobre os serviços da WSJ.'
);

const NAVY = "#0a1628";
const GOLD = "#fbbf24";

const RGPD_STORAGE_KEY = 'wsj_assistant_rgpd_v1';

const WSJAssistant = () => {
  const { language } = useLanguage();
  const currentLang = language || 'pt-BR';
  const API_URL = getAssistantApiBaseUrl();
  const uiCopy = getAssistantUiCopy(currentLang);
  
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([
    {
      id: '1',
      role: 'assistant',
      content: uiCopy.assistantGreeting,
      timestamp: new Date()
    }
  ]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef(messages);

  // Voice states
  const [voiceState, setVoiceState] = useState('idle'); // idle, requesting, recording, processing, speaking, error
  const [voiceError, setVoiceError] = useState(null);
  const [voiceLastQuestion, setVoiceLastQuestion] = useState('');
  const [voiceLastAnswer, setVoiceLastAnswer] = useState('');
  const [ttsAutoplayFailed, setTtsAutoplayFailed] = useState(false);
  
  // Refs para voice
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const ttsAudioRef = useRef(null);
  const ttsAudioUrlRef = useRef(null);
  const [ttsPlayUrl, setTtsPlayUrl] = useState(null);

  const [privacyAccepted, setPrivacyAccepted] = useState(() => {
    try {
      return typeof window !== 'undefined' && localStorage.getItem(RGPD_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [privacyCheckbox, setPrivacyCheckbox] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isOpen) return;
    const el = panelRef.current;
    const vv = window.visualViewport;
    if (!el || !vv) return;

    const apply = () => {
      if (!window.matchMedia('(max-width: 639px)').matches) {
        el.style.removeProperty('height');
        el.style.removeProperty('max-height');
        return;
      }
      const h = Math.max(140, vv.height);
      el.style.height = `${h}px`;
      el.style.maxHeight = `${h}px`;
    };

    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('orientationchange', apply);
      el.style.removeProperty('height');
      el.style.removeProperty('max-height');
    };
  }, [isOpen]);

  useEffect(() => {
    console.log('[Voice][API] base URL resolved', API_URL || '(same-origin)');
  }, [API_URL]);

  useEffect(() => {
    setMessages((prev) => {
      if (!prev.length) return prev;
      const first = prev[0];
      if (first.role !== 'assistant') return prev;
      return [{ ...first, content: uiCopy.assistantGreeting }, ...prev.slice(1)];
    });
  }, [uiCopy.assistantGreeting]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      // Cleanup audio
      if (ttsAudioRef.current) {
        ttsAudioRef.current.pause();
        ttsAudioRef.current = null;
      }
      if (ttsAudioUrlRef.current) {
        try {
          URL.revokeObjectURL(ttsAudioUrlRef.current);
        } catch {}
        ttsAudioUrlRef.current = null;
      }
      // Cleanup stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Helper functions
  const getBestMimeType = () => {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/ogg",
      "audio/mp4",
    ];
    for (const type of types) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)) return type;
    }
    return "";
  };

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        const base64 = typeof result === 'string' ? result.split(',')[1] : null;
        if (base64) resolve(base64);
        else reject(new Error('Failed to convert blob to base64'));
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

  const cleanupAudioElement = (options = { revokeUrl: true }) => {
    const current = ttsAudioRef.current;
    if (current) {
      try {
        current.pause();
        current.currentTime = 0;
        current.onplay = null;
        current.onended = null;
        current.onpause = null;
        current.onerror = null;
      } catch {}
    }
    ttsAudioRef.current = null;

    if (options.revokeUrl && ttsAudioUrlRef.current) {
      try {
        URL.revokeObjectURL(ttsAudioUrlRef.current);
      } catch {}
      ttsAudioUrlRef.current = null;
      setTtsPlayUrl(null);
    }
  };

  const getApiErrorMessage = async (response, fallback) => {
    try {
      const data = await response.json();
      if (typeof data?.detail === 'string') return `${fallback}: ${data.detail}`;
    } catch {}

    if (response.status === 405) {
      return `${fallback}: endpoint not available for POST (check Vercel /api routing and REACT_APP_BACKEND_URL).`;
    }

    return `${fallback}: ${response.status}`;
  };

  const playAudioFromUrl = async (url, autoplay, options = { userInitiated: false }) => {
    const reusingCurrentUrl = ttsAudioUrlRef.current === url;
    cleanupAudioElement({ revokeUrl: !reusingCurrentUrl });

    const audio = new Audio(url);
    console.log('[Voice][TTS] Audio element created', {
      url,
      autoplay,
      userInitiated: !!options.userInitiated,
      reusedUrl: reusingCurrentUrl
    });

    audio.preload = 'auto';
    ttsAudioRef.current = audio;
    ttsAudioUrlRef.current = url;

    let started = false;

    audio.onplay = () => {
      started = true;
      console.log('[Voice][TTS] audio.onplay fired');
      setVoiceState('speaking');
      setTtsAutoplayFailed(false);
    };

    audio.onended = () => {
      console.log('[Voice][TTS] audio.onended fired');
      setVoiceState('idle');
    };

    audio.onerror = (event) => {
      console.warn('[Voice][TTS] audio.onerror fired', event);
      setTtsAutoplayFailed(true);
      setVoiceState('idle');
    };

    if (!autoplay) {
      return;
    }

    // Watchdog timeout para mobile
    setTimeout(() => {
      if (!started) {
        console.warn('[Voice][TTS] Autoplay blocked - showing manual button');
        setTtsAutoplayFailed(true);
        setVoiceState('idle');
      }
    }, 2000);

    try {
      console.log('[Voice][TTS] Calling audio.play()', {
        userInitiated: !!options.userInitiated,
        autoplay
      });
      await audio.play();
    } catch (err) {
      console.warn('[Voice][TTS] audio.play() rejected', err);
      setTtsAutoplayFailed(true);
      setVoiceState('idle');
    }
  };

  // Voice handlers
  const startRecording = async () => {
    if (!privacyAccepted) return;
    cleanupAudioElement({ revokeUrl: true });
    setTtsPlayUrl(null);
    setTtsAutoplayFailed(false);
    setVoiceError(null);
    setVoiceLastQuestion('');
    setVoiceLastAnswer('');
    setVoiceState('requesting');

    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError(uiCopy.micUnavailable);
      setVoiceState('error');
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });
    } catch (err) {
      console.error('[Voice] getUserMedia error:', err);
      setVoiceError(uiCopy.micAccessError);
      setVoiceState('error');
      return;
    }

    streamRef.current = stream;
    audioChunksRef.current = [];

    const mimeType = getBestMimeType();
    let recorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch (err) {
      console.error('[Voice] MediaRecorder creation error:', err);
      stream.getTracks().forEach(tk => tk.stop());
      setVoiceError(uiCopy.recordStartError);
      setVoiceState('error');
      return;
    }

    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    recorder.onerror = () => {
      stream.getTracks().forEach(tk => tk.stop());
      setVoiceError(uiCopy.recordingError);
      setVoiceState('error');
    };

    recorder.start(250);
    setVoiceState('recording');
  };

  const stopRecording = async () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || voiceState !== 'recording') return;

    setVoiceState('processing');

    streamRef.current?.getTracks().forEach(tk => tk.stop());
    streamRef.current = null;

    await new Promise((resolve) => {
      recorder.onstop = () => resolve();
      recorder.requestData();
      recorder.stop();
    });

    const chunks = audioChunksRef.current;
    if (chunks.length === 0) {
      setVoiceError(uiCopy.noAudioRecorded);
      setVoiceState('error');
      return;
    }

    const mimeType = recorder.mimeType || 'audio/webm';
    const blob = new Blob(chunks, { type: mimeType });
    console.log('[Voice][Transcribe] preparing upload', {
      mimeType,
      sizeBytes: blob.size,
      chunks: chunks.length
    });

    if (blob.size < 1000) {
      setVoiceError(uiCopy.recordingTooShort);
      setVoiceState('error');
      return;
    }

    // Step 1: Transcribe
    let transcribedText;
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      formData.append('language', currentLang);
      console.log('[Voice][Transcribe] sending request', {
        endpoint: `${API_URL}/api/voice/transcribe`,
        uiLanguage: currentLang,
        transport: 'multipart/form-data'
      });

      const response = await fetch(`${API_URL}/api/voice/transcribe`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Transcription error'));
      }

      const result = await response.json();
      transcribedText = result.text?.trim() || '';

      if (!transcribedText) {
        setVoiceError(uiCopy.transcriptionEmpty);
        setVoiceState('error');
        return;
      }
    } catch (err) {
      console.error('[Voice] transcription error:', err);
      setVoiceError(uiCopy.transcriptionError);
      setVoiceState('error');
      return;
    }

    setVoiceLastQuestion(transcribedText);

    // Step 2: Send to AI and speak response
    setTtsAutoplayFailed(false);
    await sendVoiceMessage(transcribedText);

  };

  const sendVoiceMessage = async (text) => {
    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setIsVoiceLoading(true);

    try {
      const chatHistory = [...messagesRef.current, userMsg].map((m) => ({
        role: m.role,
        content: m.content
      }));

      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: chatHistory,
          language: currentLang,
          voiceMode: true
        })
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Chat error'));
      }

      const data = await response.json();
      const replyText = data.reply;

      const botMsg = {
        id: Date.now().toString(),
        role: 'assistant',
        content: replyText,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMsg]);
      setVoiceLastAnswer(replyText);

      // Step 3: TTS
      setVoiceState('processing');
      try {
        const ttsResponse = await fetch(`${API_URL}/api/voice/speak`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: replyText,
            language: currentLang
          })
        });

        if (!ttsResponse.ok) {
          throw new Error(await getApiErrorMessage(ttsResponse, 'TTS error'));
        }

        const arrayBuffer = await ttsResponse.arrayBuffer();
        const audioBlob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);

        await playAudioFromUrl(audioUrl, true, { userInitiated: false });
        setTtsPlayUrl(audioUrl);
      } catch (err) {
        console.warn('[Voice][TTS] Failed:', err);
        setTtsAutoplayFailed(true);
        setVoiceState('idle');
      }
    } catch (error) {
      console.error('Voice chat error:', error);
      const errorMsg = {
        id: Date.now().toString(),
        role: 'assistant',
        content: uiCopy.genericError,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
      setVoiceState('idle');
    } finally {
      setIsVoiceLoading(false);
    }
  };

  const handleMicButton = () => {
    if (voiceState === 'idle' || voiceState === 'error') {
      startRecording();
    } else if (voiceState === 'recording') {
      stopRecording();
    } else if (voiceState === 'speaking') {
      cleanupAudioElement({ revokeUrl: false });
      setVoiceState('idle');
    }
  };

  const handleReplayAnswer = () => {
    if (!privacyAccepted || !voiceLastAnswer || !ttsAudioUrlRef.current) return;

    console.log('[Voice][TTS] Manual replay requested from user click');
    setVoiceState('processing');
    // Mantemos audio.play() no fluxo síncrono do clique para iOS/Safari.
    playAudioFromUrl(ttsAudioUrlRef.current, true, { userInitiated: true }).catch((err) => {
      console.warn('[Voice][TTS] Manual replay error', err);
      setVoiceState('idle');
    });
  };

  const sendMessage = async () => {
    if (!privacyAccepted || !inputText.trim() || isChatLoading) return;

    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: inputText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsChatLoading(true);

    try {
      const chatHistory = [...messagesRef.current, userMsg].map((m) => ({
        role: m.role,
        content: m.content
      }));

      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: chatHistory,
          language: currentLang,
          voiceMode: false
        })
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Chat error'));
      }

      const data = await response.json();

      const botMsg = {
        id: Date.now().toString(),
        role: 'assistant',
        content: data.reply,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = {
        id: Date.now().toString(),
        role: 'assistant',
        content: uiCopy.genericError,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const openWhatsApp = () => {
    if (!privacyAccepted) return;
    window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer');
  };

  const handleAcceptPrivacy = () => {
    if (!privacyCheckbox) return;
    try {
      localStorage.setItem(RGPD_STORAGE_KEY, '1');
    } catch {}
    setPrivacyAccepted(true);
  };

  const safeBottomPad = { paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' };

  const whatsappButtonClass =
    'w-full rounded-xl px-4 py-2.5 sm:py-3 text-[13px] sm:text-sm font-semibold text-white transition-opacity hover:opacity-95 active:opacity-90 flex items-center justify-center gap-2 shrink-0 max-sm:leading-snug';
  const whatsappButtonStyle = { background: '#25D366' };

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110"
          style={{
            bottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
            right: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            background: `linear-gradient(135deg, ${NAVY}, #1a3050)`,
            border: '2px solid rgba(251, 191, 36, 0.3)',
            boxShadow: '0 8px 32px rgba(251, 191, 36, 0.2)',
            zIndex: 99990,
            pointerEvents: 'auto'
          }}
          title="Assistente WSJ AI"
        >
          <MessageCircle className="w-6 h-6 text-yellow-400" />
        </button>
      )}

      {isOpen && (
        <div
          ref={panelRef}
          className="fixed z-[100000] flex flex-col overflow-hidden shadow-2xl
            inset-0 h-[100dvh] max-h-[100dvh] w-full max-w-full rounded-none
            sm:inset-auto sm:bottom-6 sm:right-5 sm:left-auto sm:top-auto sm:h-[min(720px,90svh)]
            sm:max-h-[760px] sm:w-[min(520px,calc(100vw-2rem))] sm:max-w-[520px]
            sm:rounded-2xl sm:min-h-0 sm:shadow-[0_25px_80px_-12px_rgba(0,0,0,0.55)]"
          style={{
            background: NAVY,
            border: '1px solid rgba(251, 191, 36, 0.2)',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            pointerEvents: 'auto',
            touchAction: 'manipulation'
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
              className="flex shrink-0 items-center justify-between px-4 py-3 sm:px-5 sm:py-4"
              style={{
                background: 'rgba(251, 191, 36, 0.08)',
                borderBottom: '1px solid rgba(251, 191, 36, 0.15)'
              }}
            >
              <div className="flex min-w-0 flex-1 items-center gap-3 pr-2">
                <div
                  className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center"
                  style={{
                    background: 'rgba(251, 191, 36, 0.15)',
                    border: '1px solid rgba(251, 191, 36, 0.3)'
                  }}
                >
                  <div className="text-xl font-black sm:text-2xl">
                    <span className="text-white">W</span>
                    <span className="text-red-600">.</span>
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-sm text-yellow-400">WSJ Assistente AI</p>
                  <p className="text-xs text-white/50">Online • Chat + Voz</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  cleanupAudioElement({ revokeUrl: true });
                  setTtsPlayUrl(null);
                  setVoiceState('idle');
                  setVoiceError(null);
                  setVoiceLastQuestion('');
                  setVoiceLastAnswer('');
                  setInputText('');
                  setMessages([
                    {
                      id: '1',
                      role: 'assistant',
                      content: getAssistantUiCopy(currentLang).assistantGreeting,
                      timestamp: new Date()
                    }
                  ]);
                  setIsOpen(false);
                }}
                className="shrink-0 rounded-lg p-2 text-white/70 hover:bg-white/5 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div
              className={`flex shrink-0 border-b ${!privacyAccepted ? 'pointer-events-none opacity-55' : ''}`}
              style={{ borderColor: 'rgba(251, 191, 36, 0.15)' }}
            >
              <button
                type="button"
                onClick={() => setActiveTab('chat')}
                className="flex flex-1 items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors sm:px-4"
                style={{
                  color: activeTab === 'chat' ? GOLD : 'rgba(255, 255, 255, 0.5)',
                  background: activeTab === 'chat' ? 'rgba(251, 191, 36, 0.08)' : 'transparent',
                  borderBottom: activeTab === 'chat' ? `2px solid ${GOLD}` : '2px solid transparent'
                }}
              >
                <MessageSquare className="h-4 w-4 shrink-0" />
                Chat
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('voice')}
                className="flex flex-1 items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors sm:px-4"
                style={{
                  color: activeTab === 'voice' ? GOLD : 'rgba(255, 255, 255, 0.5)',
                  background: activeTab === 'voice' ? 'rgba(251, 191, 36, 0.08)' : 'transparent',
                  borderBottom: activeTab === 'voice' ? `2px solid ${GOLD}` : '2px solid transparent'
                }}
              >
                <Mic className="h-4 w-4 shrink-0" />
                Voz
              </button>
            </div>

            {!privacyAccepted && (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  <div
                    className="rounded-xl border px-4 py-4 sm:px-5 sm:py-5"
                    style={{
                      borderColor: 'rgba(251, 191, 36, 0.25)',
                      background: 'rgba(0, 0, 0, 0.25)'
                    }}
                  >
                    <p className="text-sm font-semibold text-yellow-400">{uiCopy.privacyTitle}</p>
                    <p className="mt-3 text-sm leading-relaxed text-white/85">{uiCopy.privacyBody}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
                      <button
                        type="button"
                        className="text-yellow-400/95 underline underline-offset-2 hover:text-yellow-300"
                        onClick={(e) => e.preventDefault()}
                      >
                        {uiCopy.privacyLinkPrivacy}
                      </button>
                      <span className="text-white/25">|</span>
                      <button
                        type="button"
                        className="text-yellow-400/95 underline underline-offset-2 hover:text-yellow-300"
                        onClick={(e) => e.preventDefault()}
                      >
                        {uiCopy.privacyLinkTerms}
                      </button>
                    </div>
                    <p className="mt-2 text-[11px] leading-snug text-white/40">{uiCopy.privacyLinksHint}</p>
                    <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-white/90">
                      <input
                        type="checkbox"
                        checked={privacyCheckbox}
                        onChange={(e) => setPrivacyCheckbox(e.target.checked)}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-white/30 bg-white/5 text-yellow-500 focus:ring-yellow-500/50"
                      />
                      <span>{uiCopy.privacyCheckbox}</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleAcceptPrivacy}
                      disabled={!privacyCheckbox}
                      className="mt-5 w-full rounded-xl px-4 py-3 text-sm font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                      style={{ background: GOLD, color: NAVY }}
                    >
                      {uiCopy.privacyAccept}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {privacyAccepted && activeTab === 'chat' && (
              <div className="flex min-h-0 flex-1 flex-col">
                <div
                  className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4"
                  style={{ background: 'rgba(0, 0, 0, 0.2)', WebkitOverflowScrolling: 'touch' }}
                >
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className="max-w-[min(100%,22rem)] rounded-2xl px-4 py-3 text-sm sm:max-w-[92%] sm:text-[15px] sm:leading-relaxed"
                        style={{
                          background:
                            msg.role === 'user'
                              ? 'rgba(251, 191, 36, 0.15)'
                              : 'rgba(255, 255, 255, 0.05)',
                          border:
                            msg.role === 'user'
                              ? '1px solid rgba(251, 191, 36, 0.3)'
                              : '1px solid rgba(255, 255, 255, 0.1)',
                          color: '#ffffff',
                          wordBreak: 'break-word'
                        }}
                      >
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {isChatLoading && (
                    <div className="flex justify-start">
                      <div className="flex gap-1 px-4 py-3">
                        <div
                          className="h-2 w-2 animate-bounce rounded-full bg-yellow-400"
                          style={{ animationDelay: '0ms' }}
                        />
                        <div
                          className="h-2 w-2 animate-bounce rounded-full bg-yellow-400"
                          style={{ animationDelay: '150ms' }}
                        />
                        <div
                          className="h-2 w-2 animate-bounce rounded-full bg-yellow-400"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div
                  className="shrink-0 border-t px-4 pt-3"
                  style={{ borderColor: 'rgba(251, 191, 36, 0.15)' }}
                >
                  <button
                    type="button"
                    onClick={openWhatsApp}
                    className={whatsappButtonClass}
                    style={whatsappButtonStyle}
                  >
                    <svg className="h-5 w-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.652a11.94 11.94 0 005.713 1.456h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    {uiCopy.whatsappCta}
                  </button>
                </div>

                <form
                  className="flex shrink-0 flex-nowrap gap-2 border-t p-3 sm:p-4"
                  style={{ ...safeBottomPad, borderColor: 'rgba(251, 191, 36, 0.15)' }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!privacyAccepted) return;
                    sendMessage();
                  }}
                >
                  <input
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder={uiCopy.chatPlaceholder}
                    disabled={isChatLoading || !privacyAccepted}
                    autoComplete="off"
                    enterKeyHint="send"
                    className="min-w-0 flex-1 rounded-lg px-3 py-2.5 text-base text-white placeholder-white/30 outline-none sm:px-4 sm:text-sm"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(251, 191, 36, 0.2)'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={isChatLoading || !inputText.trim() || !privacyAccepted}
                    className="flex h-11 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg px-3 transition-all disabled:opacity-50 sm:h-10 sm:min-h-0 sm:min-w-0 sm:px-4"
                    style={{ background: GOLD, color: NAVY }}
                    aria-label="Enviar"
                  >
                    <Send className="h-5 w-5 sm:h-4 sm:w-4" />
                  </button>
                </form>
              </div>
            )}

            {privacyAccepted && activeTab === 'voice' && (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4"
                  style={{ background: 'rgba(0, 0, 0, 0.2)', WebkitOverflowScrolling: 'touch' }}
                >
                  <div className="flex flex-col items-center gap-4">
                    <button
                      type="button"
                      onClick={handleMicButton}
                      disabled={voiceState === 'processing' || isVoiceLoading}
                      className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full transition-all disabled:opacity-50 sm:h-32 sm:w-32"
                      style={{
                        background:
                          voiceState === 'recording'
                            ? 'rgba(239, 68, 68, 0.15)'
                            : 'rgba(251, 191, 36, 0.15)',
                        border: `3px solid ${voiceState === 'recording' ? '#ef4444' : GOLD}`,
                        transform: voiceState === 'recording' ? 'scale(1.05)' : 'scale(1)',
                        boxShadow:
                          voiceState === 'recording' ? '0 0 20px rgba(239, 68, 68, 0.5)' : 'none'
                      }}
                    >
                      <Mic
                        className="h-10 w-10 sm:h-12 sm:w-12"
                        style={{ color: voiceState === 'recording' ? '#ef4444' : GOLD }}
                      />
                    </button>

                    <p className="text-center text-sm font-medium text-white/70">
                      {voiceState === 'idle' && uiCopy.voiceTapToSpeak}
                      {voiceState === 'requesting' && uiCopy.voiceRequesting}
                      {voiceState === 'recording' && uiCopy.voiceRecording}
                      {voiceState === 'processing' && uiCopy.voiceProcessing}
                      {voiceState === 'speaking' && uiCopy.voiceSpeaking}
                      {voiceState === 'error' && voiceError}
                    </p>

                    {voiceLastQuestion && (
                      <div
                        className="w-full max-w-md rounded-lg p-3"
                        style={{
                          background: 'rgba(251, 191, 36, 0.05)',
                          border: '1px solid rgba(251, 191, 36, 0.2)'
                        }}
                      >
                        <p className="mb-1 text-xs text-white/40">{uiCopy.voiceYouSaid}</p>
                        <p className="text-sm text-white" style={{ wordBreak: 'break-word' }}>
                          {voiceLastQuestion}
                        </p>
                      </div>
                    )}

                    {voiceLastAnswer && (
                      <div
                        className="w-full max-w-md max-h-[min(40vh,240px)] overflow-y-auto rounded-lg p-3 sm:max-h-[min(35vh,280px)]"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                      >
                        <p className="mb-1 text-xs text-white/40">{uiCopy.voiceAnswerLabel}</p>
                        <p className="text-sm text-white whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>
                          {voiceLastAnswer}
                        </p>
                      </div>
                    )}

                    {voiceState === 'idle' && !voiceLastQuestion && (
                      <div
                        className="rounded-lg p-3"
                        style={{
                          background: 'rgba(251, 191, 36, 0.05)',
                          border: '1px solid rgba(251, 191, 36, 0.1)'
                        }}
                      >
                        <p className="text-center text-xs text-white/50">
                          {uiCopy.voiceHintLine1}
                          <br />
                          {uiCopy.voiceHintLine2}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="shrink-0 space-y-2 border-t px-4 pt-3"
                  style={{ ...safeBottomPad, borderColor: 'rgba(251, 191, 36, 0.15)' }}
                >
                  <button
                    type="button"
                    onClick={openWhatsApp}
                    className={whatsappButtonClass}
                    style={whatsappButtonStyle}
                  >
                    <svg className="h-5 w-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.652a11.94 11.94 0 005.713 1.456h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    {uiCopy.whatsappCta}
                  </button>

                  {voiceLastAnswer && ttsPlayUrl && (
                    <button
                      type="button"
                      onClick={handleReplayAnswer}
                      className="w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all hover:opacity-95 active:opacity-90"
                      style={{
                        background: GOLD,
                        color: NAVY,
                        boxShadow: ttsAutoplayFailed ? '0 0 0 2px rgba(251,191,36,0.5)' : undefined
                      }}
                    >
                      🔊 {uiCopy.listenReply}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default WSJAssistant;
